import { CarrierName, SuretyAccountStatus, type SuretyAccountUnderwritingId } from "@inrev/common";
import { Option } from "@inrev/core";
import type { AdminAccountBondingLines } from "@portal/api/account/admin";
import { CARRIER_ROW_NAMES } from "@portal/components/features/bonding-line/carrierDisplay";
import {
	createContext,
	Dispatch,
	ReactNode,
	useContext,
	useEffect,
	useMemo,
	useReducer,
	useRef,
} from "react";
import type { AccountIdentity } from "../../../../../../shared/account/AccountHeader";
import {
	BondingLineAction,
	BondingLineDraft,
	BondingLineDrafts,
	BondingLineMode,
	BondingLineState,
	bondingLineReducer,
	draftsEqual,
	isStateDirty,
} from "./reducer";

const buildCarrierDraft = (
	bondingLines: Option<AdminAccountBondingLines>,
	carrierName: CarrierName,
): BondingLineDraft => {
	const inrev = bondingLines?.bondingLine;
	const bondingLine = bondingLines?.carrierBondingLines.find(
		(line) => line.carrierName === carrierName,
	);
	return {
		rate: bondingLine?.rate ?? null,
		singleLimit: bondingLine?.singleLimit ?? null,
		aggregateLimit: bondingLine?.aggregateLimit ?? null,
		match:
			Option.isSome(inrev) &&
			bondingLine !== undefined &&
			bondingLine.rate === inrev.rate &&
			bondingLine.singleLimit === inrev.singleLimit &&
			bondingLine.aggregateLimit === inrev.aggregateLimit,
	};
};

export const buildDrafts = (bondingLines: Option<AdminAccountBondingLines>): BondingLineDrafts => {
	const inrev = bondingLines?.bondingLine;
	return {
		inrev: {
			rate: inrev?.rate ?? null,
			singleLimit: inrev?.singleLimit ?? null,
			aggregateLimit: inrev?.aggregateLimit ?? null,
			match: false,
			isPublished: inrev?.isPublished ?? false,
		},
		carriers: Object.fromEntries(
			CARRIER_ROW_NAMES.map((carrierName) => [
				carrierName,
				buildCarrierDraft(bondingLines, carrierName),
			]),
		),
	};
};

// Declined accounts are reviewable too: an admin can edit the lines and approve,
// reversing the decline through the same approval flow used in review.
type BondingLineAccount = Pick<AccountIdentity, "id" | "status">;

export const modeForAccount = (account: BondingLineAccount): BondingLineMode =>
	account.status === SuretyAccountStatus.review || account.status === SuretyAccountStatus.declined
		? "review"
		: "readonly";

const buildInitialState = ({
	account,
	bondingLines,
}: {
	account: BondingLineAccount;
	bondingLines: Option<AdminAccountBondingLines>;
}): BondingLineState => {
	const drafts = buildDrafts(bondingLines);
	return { ...drafts, mode: modeForAccount(account), baseline: drafts, submitAttempted: false };
};

type BondingLineContextValue = {
	state: BondingLineState;
	dispatch: Dispatch<BondingLineAction>;
};

const BondingLineStateContext = createContext<BondingLineContextValue | null>(null);

type BondingLineStateProviderProps = {
	account: BondingLineAccount;
	bondingLines: Option<AdminAccountBondingLines>;
	underwritingId: SuretyAccountUnderwritingId;
	children: ReactNode;
};

export const BondingLineStateProvider = ({
	account,
	bondingLines,
	underwritingId,
	children,
}: BondingLineStateProviderProps) => {
	const [state, dispatch] = useReducer(
		bondingLineReducer,
		{ account, bondingLines },
		buildInitialState,
	);

	const incomingDrafts = useMemo(() => buildDrafts(bondingLines), [bondingLines]);
	const accountStatus = account.status;
	const lastSeenRef = useRef({
		accountStatus,
		underwritingId,
		drafts: incomingDrafts,
	});
	useEffect(() => {
		const lastSeen = lastSeenRef.current;
		const identityUnchanged =
			lastSeen.accountStatus === accountStatus &&
			lastSeen.underwritingId === underwritingId &&
			draftsEqual(lastSeen.drafts, incomingDrafts);
		if (identityUnchanged) return;

		// A refetch of the bonding lines (or a mode change) can land while the admin is mid-edit.
		// Adopt the incoming data only when there's nothing unsaved, or when it already matches the
		// current edits (e.g. the admin's own save landing). Otherwise keep the in-progress edits
		// rather than silently discarding them — `lastSeenRef` stays stale so the reset retries once
		// the edits are saved, cancelled, or the next refetch arrives.
		if (isStateDirty(state) && !draftsEqual(state, incomingDrafts)) return;

		lastSeenRef.current = {
			accountStatus,
			underwritingId,
			drafts: incomingDrafts,
		};
		dispatch({
			type: "reset_from_account",
			drafts: incomingDrafts,
			mode: modeForAccount(account),
		});
	}, [accountStatus, underwritingId, account, incomingDrafts, state]);

	const value = useMemo<BondingLineContextValue>(() => ({ state, dispatch }), [state]);

	return (
		<BondingLineStateContext.Provider value={value}>{children}</BondingLineStateContext.Provider>
	);
};

export const useBondingLineState = (): BondingLineState => {
	const context = useContext(BondingLineStateContext);
	if (context === null) {
		throw new Error("useBondingLineState must be used within a BondingLineStateProvider");
	}
	return context.state;
};

export const useBondingLineDispatch = (): Dispatch<BondingLineAction> => {
	const context = useContext(BondingLineStateContext);
	if (context === null) {
		throw new Error("useBondingLineDispatch must be used within a BondingLineStateProvider");
	}
	return context.dispatch;
};
